/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Goal,
  Topic,
  Lesson,
  Resource,
  SaferDialogue,
  Intro,
  Study,
  Survey,
  App,
  LearnerType,
} from "@ictlearningsciences/pal3-core/types";
import {
  fetchSaferDialogue,
  fetchIntros,
  fetchStudies,
  fetchDialogueMessages,
  fetchGql,
  fetchApp,
} from "@ictlearningsciences/pal3-core/api";
import { getFormattedDate } from "@ictlearningsciences/pal3-core/helpers";
import { DialogueMessage } from "@ictlearningsciences/pal3-core/types";
import {
  SURVEY_QUESTION,
  SURVEY_QUESTION_LIKERT,
  SURVEY_QUESTION_OPEN,
} from "@ictlearningsciences/pal3-core/api/queries";
import { LoadingState, LoadStatus } from "../../../helpers";
import { FOCUS_QUERY, GOAL_QUERY, LESSON_QUERY, TOPIC_QUERY } from "./queries";

export interface DataState {
  learnerTypes: Record<string, LearnerType>;
  learnerTypesLoadState: LoadingState;
  // goal data
  goals: Record<string, Goal>;
  topics: Record<string, Topic>;
  goalsLoadState: LoadingState;
  // lesson data
  lessons: Record<string, Lesson>;
  resources: Record<string, Resource>;
  lessonsLoadState: Record<string, LoadingState>;
  // dialogue data
  saferDialogues: SaferDialogue[];
  dialogueMessages: DialogueMessage[];
  dialogueLoadState: LoadingState;
  // study data
  intros: Intro[];
  studies: Study[];
  studyLoadState: LoadingState;
  // survey data
  surveys: Survey[];
  surveyLoadState: LoadingState;
  // app data
  app?: App;
  appLoadState: LoadingState;
}

const initialState: DataState = {
  learnerTypes: {},
  learnerTypesLoadState: { status: LoadStatus.NONE },
  // goal data
  goals: {},
  topics: {},
  goalsLoadState: { status: LoadStatus.NONE },
  // lesson data
  lessons: {},
  resources: {},
  lessonsLoadState: {},
  // dialogue data
  saferDialogues: [],
  dialogueMessages: [],
  dialogueLoadState: { status: LoadStatus.NONE },
  // study data
  intros: [],
  studies: [],
  studyLoadState: { status: LoadStatus.NONE },
  // survey data
  surveys: [],
  surveyLoadState: { status: LoadStatus.NONE },
  // app data
  app: undefined,
  appLoadState: { status: LoadStatus.NONE },
};

/** Actions */
export const loadGoals = createAsyncThunk(
  "data/goals",
  async (): Promise<Goal[]> => {
    const result = await fetchGql<{ goals: Goal[] }>({
      query: `
        query Goals {
          goals {
            ${GOAL_QUERY}
            focusList {
              ${FOCUS_QUERY}
              id
              name
              desc
              topics {
                ${TOPIC_QUERY}
              }
            }
          }
        }
      `,
    });
    return result.goals;
  }
);

export const loadLessons = createAsyncThunk(
  "data/lessons",
  async (id: string): Promise<Goal> => {
    const result = await fetchGql<{ goal: Goal }>({
      query: `
        query Goal {
          goal(id: "${id}") {
            ${GOAL_QUERY}
            focusList {
              ${FOCUS_QUERY}
              id
              name
              desc
              topics {
                ${TOPIC_QUERY}
                lessons {
                  ${LESSON_QUERY}
                }                
              }
            }
          }
        }
      `,
    });
    return result.goal;
  }
);

export const loadLearnerTypes = createAsyncThunk(
  "data/learnerTypes",
  async (): Promise<LearnerType[]> => {
    const result = await fetchGql<{ learnerTypes: LearnerType[] }>({
      query: `
        query LearnerTypes {
          learnerTypes {
            id
            alias
            name
            desc
            characteristics
          }
        }
      `,
    });
    return result.learnerTypes;
  }
);

export const loadDialogue = createAsyncThunk(
  "data/dialogue",
  async (): Promise<{
    saferDialogues: SaferDialogue[];
    dialogueMessages: DialogueMessage[];
  }> => {
    const saferDialogues = await fetchSaferDialogue();
    const dialogueMessages = await fetchDialogueMessages();
    return {
      saferDialogues,
      dialogueMessages,
    };
  }
);

export const loadStudies = createAsyncThunk(
  "data/loadStudies",
  async (): Promise<{ intros: Intro[]; studies: Study[] }> => {
    const intros = await fetchIntros(undefined);
    const studies = await fetchStudies(undefined);
    return { intros, studies };
  }
);

export const loadSurveys = createAsyncThunk(
  "data/loadSurveys",
  async (): Promise<Survey[]> => {
    const result = await fetchGql<{ surveys: Survey[] }>({
      query: `
        query Surveys {
          surveys {
            alias
            displayName
            specialEventConditions {
              variable
              operator
              value
            }
            questions {
              ... on Question {
                ${SURVEY_QUESTION}
              }
              ... on OpenResponseQuestion {
                ${SURVEY_QUESTION}
                ${SURVEY_QUESTION_OPEN}
              }
              ... on MCQuestion {
                ${SURVEY_QUESTION}
                randomizeChoices
                selectMultiple
                choices {
                  text
                  question
                  deltas {
                    variableName
                    delta
                  }
                  event {
                    type
                    value
                  }
                }
              }
              ... on LikertQuestion {
                ${SURVEY_QUESTION}
                ${SURVEY_QUESTION_LIKERT}
              }
            }
          }
        }
      `,
    });
    return result.surveys;
  }
);

export const loadApp = createAsyncThunk(
  "data/loadApp",
  async (): Promise<App> => {
    return await fetchApp("web");
  }
);

export const dataSlice = createSlice({
  name: "data",
  initialState: initialState,
  reducers: {
    clearCache: (state) => {
      state.learnerTypes = initialState.learnerTypes;
      state.learnerTypesLoadState = initialState.learnerTypesLoadState;
      state.goals = initialState.goals;
      state.topics = initialState.topics;
      state.goalsLoadState = initialState.goalsLoadState;
      state.lessons = initialState.lessons;
      state.resources = initialState.resources;
      state.lessonsLoadState = initialState.lessonsLoadState;
      state.saferDialogues = initialState.saferDialogues;
      state.dialogueMessages = initialState.dialogueMessages;
      state.dialogueLoadState = initialState.dialogueLoadState;
      state.intros = initialState.intros;
      state.studies = initialState.studies;
      state.studyLoadState = initialState.studyLoadState;
      state.surveys = initialState.surveys;
      state.surveyLoadState = initialState.surveyLoadState;
      state.app = initialState.app;
      state.appLoadState = initialState.appLoadState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadGoals.pending, (state) => {
        state.goalsLoadState = {
          ...state.goalsLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadGoals.fulfilled, (state, action) => {
        const goals = action.payload;
        const topics = goals.reduce((acc: Topic[], cur: Goal) => {
          cur.focusList.forEach((focus) => {
            focus.topics.forEach((topic) => {
              acc.push(topic);
            });
          });
          return acc;
        }, []);
        state.goals = {};
        for (const goal of goals) {
          state.goals[goal.id] = {
            ...goal,
            focusList: goal.focusList.map((f) => ({
              ...f,
              topics: f.topics.map((t) => ({
                ...t,
                lessons: state.topics[t.id] ? state.topics[t.id].lessons : [],
              })),
            })),
          };
        }
        for (const topic of topics) {
          state.topics[topic.id] = {
            ...topic,
            lessons: state.topics[topic.id]?.lessons || [],
          };
        }
        state.goalsLoadState = {
          ...state.goalsLoadState,
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadGoals.rejected, (state, action) => {
        state.goalsLoadState = {
          ...state.goalsLoadState,
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })

      .addCase(loadLessons.pending, (state, action) => {
        try {
          state.lessonsLoadState[action.meta.arg] = {
            ...state.lessonsLoadState[action.meta.arg],
            status: LoadStatus.IN_PROGRESS,
            startedAt: getFormattedDate(),
            error: undefined,
          };
        } catch {
          state.lessonsLoadState = initialState.lessonsLoadState;
        }
      })
      .addCase(loadLessons.fulfilled, (state, action) => {
        const goal = action.payload;
        state.goals[goal.id] = goal;
        for (const focus of goal.focusList) {
          for (const topic of focus.topics) {
            state.topics[topic.id] = topic;
            for (const lesson of topic.lessons) {
              state.lessons[lesson.id] = lesson;
              for (const resource of lesson.resources) {
                state.resources[resource.id] = resource;
              }
            }
          }
        }
        state.lessonsLoadState[action.meta.arg] = {
          ...state.lessonsLoadState[action.meta.arg],
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadLessons.rejected, (state, action) => {
        try {
          state.lessonsLoadState[action.meta.arg] = {
            ...state.lessonsLoadState[action.meta.arg],
            status: LoadStatus.FAILED,
            failedAt: getFormattedDate(),
            error: action.error.message,
          };
        } catch {
          state.lessonsLoadState = initialState.lessonsLoadState;
        }
      })

      .addCase(loadDialogue.pending, (state) => {
        state.dialogueLoadState = {
          ...state.dialogueLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadDialogue.fulfilled, (state, action) => {
        state.saferDialogues = action.payload.saferDialogues;
        state.dialogueMessages = action.payload.dialogueMessages;
        state.dialogueLoadState = {
          ...state.dialogueLoadState,
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadDialogue.rejected, (state, action) => {
        state.dialogueLoadState = {
          ...state.dialogueLoadState,
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })

      .addCase(loadStudies.pending, (state) => {
        state.studyLoadState = {
          ...state.studyLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadStudies.fulfilled, (state, action) => {
        state.intros = action.payload.intros;
        state.studies = action.payload.studies;
        state.studyLoadState = {
          ...state.studyLoadState,
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadStudies.rejected, (state, action) => {
        state.studyLoadState = {
          ...state.studyLoadState,
          status: LoadStatus.FAILED,
          failedAt: getFormattedDate(),
          error: action.error.message,
        };
      })

      .addCase(loadSurveys.pending, (state) => {
        state.surveyLoadState = {
          ...state.surveyLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadSurveys.fulfilled, (state, action) => {
        state.surveys = action.payload;
        state.surveyLoadState = {
          ...state.surveyLoadState,
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadSurveys.rejected, (state) => {
        state.surveyLoadState = {
          ...state.surveyLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })

      .addCase(loadApp.pending, (state) => {
        state.appLoadState = {
          ...state.appLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadApp.fulfilled, (state, action) => {
        state.app = action.payload;
        state.appLoadState = {
          ...state.appLoadState,
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadApp.rejected, (state) => {
        state.appLoadState = {
          ...state.appLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })

      .addCase(loadLearnerTypes.pending, (state) => {
        state.learnerTypesLoadState = {
          ...state.learnerTypesLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadLearnerTypes.fulfilled, (state, action) => {
        const learnerTypes = action.payload;
        state.learnerTypes = {};
        for (const learnerType of learnerTypes) {
          state.learnerTypes[learnerType.alias] = learnerType;
        }
        state.learnerTypesLoadState = {
          ...state.learnerTypesLoadState,
          status: LoadStatus.DONE,
          endedAt: getFormattedDate(),
          error: undefined,
        };
      })
      .addCase(loadLearnerTypes.rejected, (state) => {
        state.learnerTypesLoadState = {
          ...state.learnerTypesLoadState,
          status: LoadStatus.IN_PROGRESS,
          startedAt: getFormattedDate(),
          error: undefined,
        };
      });
  },
});

export const { clearCache } = dataSlice.actions;

export default dataSlice.reducer;
